import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { AuthForm } from 'components/AuthForm';
import { Row } from 'components/UI/Row';
import { Column } from 'components/UI/Column';
import { Info } from 'components/Info/Info';
import { History } from 'history';
import { AppDispatch } from 'store/store';
import { RootState } from 'store/rootReducer';
import { SignInSchema, SignInFormProps } from './SignInSchema';
import { changeAuthPage, signIn } from './auth';

const SignIn = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { search } = useLocation();
  const history: History = useHistory();
  const { register, handleSubmit, errors, setError } = useForm<SignInFormProps>({
    mode: 'onBlur',
    resolver: yupResolver(SignInSchema)
  });

  const authError = useSelector((state: RootState) => state.auth.authError);
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    if (authError) {
      setError('email', { message: '' });
      setError('password', { message: '' });
    }
  }, [authError, setError]);

  const onSubmit = useCallback(
    (formData: SignInFormProps) => {
      dispatch(signIn(formData, history, queryParams.get('redirect') || null));
    },
    [dispatch, history]
  );

  return (
    <>
      <AuthForm onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          helperText={t(get(errors, 'email.message'))}
          error={!isEmpty(errors.email)}
          label={t('label.email')}
          inputRef={register}
          name="email"
          type="email"
          style={{ marginBottom: '16px', width: '400px' }}
          variant="outlined"
        />

        <TextField
          helperText={t(get(errors, 'password.message'))}
          error={!isEmpty(errors.password)}
          label={t('label.password')}
          inputRef={register}
          style={{ marginBottom: '16px', width: '400px' }}
          name="password"
          type="password"
          variant="outlined"
        />
        {authError && <FormHelperText style={{ color: '#f44336' }}>{authError}</FormHelperText>}

        <Row justifyContent="flex-end" width="400px">
          <Button variant="contained" color="primary" type="submit" style={{ height: '44px', width: '128px' }}>
            {t('home.sign in')}
          </Button>
        </Row>
      </AuthForm>
      <Column margin="70px auto 0" width="400px">
        <Button
          color="primary"
          style={{ margin: '32px 0' }}
          onClick={() => {
            dispatch(changeAuthPage());
            history.push('/auth/recover_password');
          }}
        >
          {t('home.recover password')}
        </Button>
        <Button
          color="primary"
          onClick={() => {
            dispatch(changeAuthPage());
            history.push('/auth/signup');
          }}
        >
          {t('home.sign up')}
        </Button>
      </Column>
      <Info />
    </>
  );
};

export { SignIn };
